// context/AuthContext.js

import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  getIdToken,
} from "firebase/auth";
import { auth, db } from "../firebase.config";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";

const AuthContext = createContext();
const SESSION_DURATION = 24 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if session is still valid (within 24 hours of login)
  const checkSessionValidity = useCallback(() => {
    const loginTimestamp = localStorage.getItem("loginTimestamp");

    if (loginTimestamp) {
      const elapsed = Date.now() - parseInt(loginTimestamp, 10);

      if (elapsed >= SESSION_DURATION) {
        // Session expired - force logout
        console.log("Session expired after 24 hours. Logging out...");
        signOut(auth);
        localStorage.clear();
        sessionStorage.clear();
        setUser(null);
        setToken(null);
        return false;
      }
    }
    return true;
  }, []);

  // Watch auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Check session validity before setting user
        if (!checkSessionValidity()) {
          setLoading(false);
          navigate("/login");
          return;
        }

        const token = await firebaseUser.getIdToken();
        setUser(firebaseUser);
        setToken(token);

        localStorage.setItem("authToken", token);
        localStorage.setItem("authUser", JSON.stringify(firebaseUser));
      } else {
        setUser(null);
        setToken(null);
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [checkSessionValidity, navigate]);

  // Periodic session check & visibility change detection
  useEffect(() => {
    // Check session validity on mount
    checkSessionValidity();

    // Check every 5 minutes while app is open
    const interval = setInterval(
      () => {
        if (!checkSessionValidity()) {
          navigate("/login");
        }
      },
      5 * 60 * 1000,
    );

    // Check when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (!checkSessionValidity()) {
          navigate("/login");
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkSessionValidity, navigate]);

  // ✅ SIGNUP: accepts full user info
  const signup = useCallback(
    async ({ email, password, firstName, lastName }) => {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const firebaseUser = userCredential.user;

      // Store in Firestore
      await setDoc(doc(db, "users", firebaseUser.uid), {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        firstName: firstName,
        lastName: lastName,
        role: "user",
        subscription_status: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      const token = await getIdToken(firebaseUser);
      setUser(firebaseUser);
      setToken(token);

      // Store login timestamp for 24-hour session tracking
      localStorage.setItem("loginTimestamp", Date.now().toString());
      localStorage.setItem("authToken", token);
      localStorage.setItem("authUser", JSON.stringify(firebaseUser));

      navigate("/our-plans");
    },
    [navigate],
  );

  // Login function
  const login = useCallback(
    async ({ email, password }) => {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const firebaseUser = userCredential.user;
      const token = await getIdToken(firebaseUser);

      setUser(firebaseUser);
      setToken(token);

      // Store login timestamp for 24-hour session tracking
      localStorage.setItem("loginTimestamp", Date.now().toString());
      localStorage.setItem("authToken", token);
      localStorage.setItem("authUser", JSON.stringify(firebaseUser));

      navigate("/our-plans");
    },
    [navigate],
  );

  const logout = useCallback(async () => {
    if (auth.currentUser) {
      try {
        await updateDoc(doc(db, "users", auth.currentUser.uid), {
          longInputEnabled: false,
        });
      } catch (error) {
        console.error("Error resetting longInputEnabled on logout:", error);
      }
    }
    await signOut(auth);
    localStorage.clear();
    sessionStorage.clear();
    const dfMessenger = document.querySelector("df-messenger");
    if (dfMessenger) {
      dfMessenger.clearStorage();
      dfMessenger.clearAuthentication();
    }
    setUser(null);
    setToken(null);
    navigate("/login");
  }, [navigate]);

  return (
    <AuthContext.Provider
      value={{ user, token, login, signup, logout, loading }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
